import React, { useState, useEffect, useCallback, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  getPaymentMethods,
  generateUPIQR,
  processUPIIntent,
  verifyPayment,
  processCardPayment,
  processCOD,
  applyGiftCard,
  getOrderDetails,
  toastSuccess,
  toastError,
  toastInfo,
  toastWarning,
} from "../../api/services";
import useAuth from "../../Features/Utils/useAuth";
import { validateUPI } from "../../Features/Utils/upiValidation";
import OrderSummaryComponent from "./OrderSummaryComponent";

// UPI Apps Configuration
const UPI_APPS = [
  {
    id: "google_pay",
    name: "Google Pay",
    package: "com.google.android.apps.nbu.pay",
    scheme: "tez://upi/pay",
    icon: "fab fa-google-pay",
    color: "#4285f4",
  },
  {
    id: "phonepe",
    name: "PhonePe",
    package: "com.phonepe.app",
    scheme: "phonepe://pay",
    icon: "fas fa-mobile-alt",
    color: "#5f259f",
  },
  {
    id: "paytm",
    name: "Paytm",
    package: "net.one97.paytm",
    scheme: "paytmmp://pay",
    icon: "fas fa-rupee-sign",
    color: "#00baf2",
  },
  {
    id: "amazon_pay",
    name: "Amazon Pay",
    package: "in.amazon.mShop.android.shopping",
    scheme: "amazonpay://pay",
    icon: "fab fa-amazon-pay",
    color: "#ff9900",
  },
];

function PaymentPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderIdFromUrl = searchParams.get("orderId");

  // Device detection
  const [isMobile, setIsMobile] = useState(false);
  const [showDesktopQR, setShowDesktopQR] = useState(false);
  const [desktopUPILink, setDesktopUPILink] = useState("");

  // Refs
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);
  const blurListenerRef = useRef(null);

  // State declarations
  const [activeMethod, setActiveMethod] = useState("upi_qr"); // Changed default to UPI QR
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [cvvError, setCvvError] = useState("");
  const [cardNumberError, setCardNumberError] = useState("");
  const [expiryError, setExpiryError] = useState("");
  const [giftCode, setGiftCode] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [giftDiscount, setGiftDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showUPIModal, setShowUPIModal] = useState(false);
  const [showAppSelector, setShowAppSelector] = useState(false);
  const [upiVpa, setUpiVpa] = useState("");
  const [paymentMethodsList, setPaymentMethodsList] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [appInstallStatus, setAppInstallStatus] = useState({});
  const [order, setOrder] = useState({
    items: [],
    totalPrice: 0,
    totalAmount: 0,
    totalDiscount: 0,
    originalTotal: 0,
    shippingCharge: 0,
    orderId: 0,
  });

  // Detect device
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    setIsMobile(mobileRegex.test(userAgent));
  }, []);

  // Cleanup function
  const cleanupIframe = useCallback(() => {
    try {
      if (iframeRef.current && iframeRef.current.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
    } catch (error) {
      console.log("Iframe cleanup error:", error);
    }
    iframeRef.current = null;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (blurListenerRef.current) {
      window.removeEventListener("blur", blurListenerRef.current);
      blurListenerRef.current = null;
    }
  }, []);

  // Check if app is installed
  const checkAppInstallation = useCallback(
    async (app) => {
      if (!isMobile) return false;

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          resolve(false);
        }, 800);

        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = app.scheme;
        document.body.appendChild(iframe);

        const handleBlur = () => {
          clearTimeout(timeout);
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
          resolve(true);
        };

        window.addEventListener("blur", handleBlur, { once: true });

        setTimeout(() => {
          clearTimeout(timeout);
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
          window.removeEventListener("blur", handleBlur);
          resolve(false);
        }, 1000);
      });
    },
    [isMobile],
  );

  // Check all apps installation status (only on mobile)
  const checkAllApps = useCallback(async () => {
    if (!isMobile) return;

    const status = {};
    for (const app of UPI_APPS) {
      const isInstalled = await checkAppInstallation(app);
      status[app.id] = isInstalled;
    }
    setAppInstallStatus(status);
  }, [checkAppInstallation, isMobile]);

  useEffect(() => {
    if (isMobile) {
      checkAllApps();
    }
    return () => {
      cleanupIframe();
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [checkAllApps, cleanupIframe, pollingInterval, isMobile]);

  // Validation functions
  const validateCardNumber = (number) => {
    const cleanNumber = number.replace(/\s/g, "");
    if (!/^\d{16}$/.test(cleanNumber)) {
      setCardNumberError("Card number must be 16 digits");
      return false;
    }
    let sum = 0;
    let isEven = false;
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber.charAt(i), 10);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    if (sum % 10 !== 0) {
      setCardNumberError("Invalid card number");
      return false;
    }
    setCardNumberError("");
    return true;
  };

  const validateExpiry = (expiryStr) => {
    const [month, year] = expiryStr.split(" / ");
    if (!month || !year) {
      setExpiryError("Invalid expiry date");
      return false;
    }
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    const expMonth = Number.parseInt(month);
    const expYear = Number.parseInt(year);

    if (expMonth < 1 || expMonth > 12) {
      setExpiryError("Invalid month");
      return false;
    }
    if (
      expYear < currentYear ||
      (expYear === currentYear && expMonth < currentMonth)
    ) {
      setExpiryError("Card has expired");
      return false;
    }
    setExpiryError("");
    return true;
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(" ").slice(0, 19) : cleaned;
  };

  const formatExpiry = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 4)
      return `${cleaned.slice(0, 2)} / ${cleaned.slice(2, 4)}`;
    return `${cleaned.slice(0, 2)} / ${cleaned.slice(2, 4)}`;
  };

  const fetchOrderDetails = useCallback(async () => {
    if (!orderIdFromUrl) {
      toastError("No order ID. Please start from checkout.");
      navigate("/cart");
      return;
    }
    try {
      const response = await getOrderDetails(orderIdFromUrl, user?.id);
      if (response?.data?.success) {
        const orderData = response.data.data;

        // Convert all numeric values to numbers
        const originalTotal = Number(orderData.originalTotal) || 0;
        const totalDiscount = Number(orderData.totalDiscount) || 0;
        let totalAmount = Number(orderData.totalAmount) || 0;
        let shippingCharge = Number(orderData.shippingCharge) || 0;
        const platformFee = Number(orderData.platformFee) || 0;

        // Calculate subtotal after product discounts
        const subtotal = originalTotal - totalDiscount;

        // Check if shipping is already included in totalAmount
        const isShippingIncluded = totalAmount > subtotal;

        // Calculate shipping if not included
        if (!isShippingIncluded && shippingCharge === 0) {
          // Free shipping above ₹500, else ₹60
          shippingCharge = subtotal > 500 ? 0 : 60;
        }

        // Calculate correct total amount including all components
        const correctTotalAmount = subtotal + shippingCharge + platformFee;

        setOrder({
          items: Array.isArray(orderData.items) ? orderData.items : [],
          totalPrice: Number(orderData.totalPrice) || 0,
          totalAmount: correctTotalAmount,
          totalDiscount: totalDiscount,
          originalTotal: originalTotal,
          shippingCharge: shippingCharge,
          platformFee: platformFee,
          orderId: orderData.id || orderIdFromUrl,
        });

        console.log("Order Data Fixed:", {
          originalTotal,
          totalDiscount,
          subtotal,
          shippingCharge,
          platformFee,
          correctTotalAmount,
        });
      } else {
        toastError(response?.data?.message || "Failed to load order details");
        setTimeout(() => navigate("/cart"), 2000);
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toastError("Could not load order details");
      setTimeout(() => navigate("/cart"), 2000);
    }
  }, [orderIdFromUrl, navigate, user?.id]);

  const getCurrentTotal = () => {
    const originalTotal = Number(order.originalTotal) || 0;
    const totalDiscount = Number(order.totalDiscount) || 0;
    const shippingCharge = Number(order.shippingCharge) || 0;
    const platformFee = Number(order.platformFee) || 0;
    const giftDiscountValue = Number(giftDiscount) || 0;

    const subtotal = originalTotal - totalDiscount;
    const totalWithShipping = subtotal + shippingCharge;
    const totalWithPlatform = totalWithShipping + platformFee;
    const finalTotal = Math.max(totalWithPlatform - giftDiscountValue, 0);

    return finalTotal;
  };

  const currentTotal = getCurrentTotal();

  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await getPaymentMethods();
      if (response?.data?.success) {
        setPaymentMethodsList(response.data.data.methods);
      } else {
        setPaymentMethodsList([
          {
            id: "card",
            name: "Credit/Debit Card",
            enabled: true,
            description: "Visa, Mastercard, RuPay",
          },
          {
            id: "upi_qr",
            name: "UPI QR Code",
            enabled: true,
            description: "Scan with any UPI app",
          },
          {
            id: "upi_intent",
            name: "UPI Intent",
            enabled: true,
            description: "Google Pay, PhonePe",
          },
          {
            id: "cod",
            name: "Cash on Delivery",
            enabled: true,
            description: "Pay on delivery",
          },
          {
            id: "gift",
            name: "Gift Card",
            enabled: true,
            description: "Apply gift card",
          },
        ]);
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
    }
  }, []);

  useEffect(() => {
    if (orderIdFromUrl) {
      fetchOrderDetails();
      loadPaymentMethods();
    } else {
      toastError("No order ID. Please start from checkout.");
      navigate("/cart");
    }
  }, [fetchOrderDetails, loadPaymentMethods, orderIdFromUrl, navigate]);

  const updateActiveTab = (method) => {
    setActiveMethod(method);
    setCvvError("");
    setCardNumberError("");
    setExpiryError("");
    setQrCode(null);
    setPaymentStatus(null);
    setTransactionId(null);
    setShowDesktopQR(false);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Polling for payment confirmation
  const startPolling = useCallback(
    (txnId, maxAttempts = 30) => {
      if (pollingInterval) clearInterval(pollingInterval);

      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        try {
          const result = await verifyPayment(txnId);

          if (result?.data?.success || result?.success) {
            const paymentData = result.data || result;
            const status = paymentData.data?.status || paymentData.status;

            if (status === "completed") {
              clearInterval(interval);
              setPollingInterval(null);
              setPaymentStatus("completed");
              toastSuccess("Payment verified successfully!");
              navigate(
                `/order-confirmation?orderId=${order.orderId}&transactionId=${txnId}`,
              );
            } else if (status === "failed") {
              clearInterval(interval);
              setPollingInterval(null);
              setPaymentStatus("failed");
              toastError("Payment failed. Please try again.");
            }
          }

          if (attempts >= maxAttempts) {
            clearInterval(interval);
            setPollingInterval(null);
            toastWarning(
              "Payment verification timeout. Please check your payment status.",
            );
          }
        } catch (error) {
          console.error("Polling error:", error);
          if (attempts >= maxAttempts) {
            clearInterval(interval);
            setPollingInterval(null);
          }
        }
      }, 3000);

      setPollingInterval(interval);
    },
    [order.orderId, navigate, pollingInterval],
  );

  // UPI QR
  const handleGenerateUPIQR = async () => {
    if (currentTotal <= 0) {
      toastError("Invalid amount");
      return;
    }
    setLoading(true);

    try {
      const result = await generateUPIQR(currentTotal, order.orderId);
      if (result?.success) {
        setQrCode(result.data.qr_code);
        setTransactionId(result.data.transaction_id);
        toastSuccess("QR Code generated! Scan to pay.");
        startPolling(result.data.transaction_id);
      } else {
        toastError(result?.message || "Failed to generate QR");
        setPaymentStatus("failed");
      }
    } catch (error) {
      console.error("QR generation error:", error);
      toastError("QR generation failed");
      setPaymentStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  // Generate UPI Intent URL
  const generateUPIIntentUrl = (transactionId, vpa, amount, app = null) => {
    const payeeVPA = "girorganic@okhdfcbank";
    const payeeName = "GirOrganic";

    const baseParams = `pa=${encodeURIComponent(payeeVPA)}&pn=${encodeURIComponent(payeeName)}&am=${amount}&cu=INR&tn=${encodeURIComponent(transactionId)}&puid=${encodeURIComponent(vpa)}`;

    if (app) {
      switch (app.id) {
        case "google_pay":
          return `tez://upi/pay?${baseParams}`;
        case "phonepe":
          return `phonepe://pay?${baseParams}`;
        case "paytm":
          return `paytmmp://pay?${baseParams}`;
        case "amazon_pay":
          return `amazonpay://pay?${baseParams}`;
        default:
          return `upi://pay?${baseParams}`;
      }
    }
    return `upi://pay?${baseParams}`;
  };

  // Handle UPI Intent Payment
  const handleUPIPayment = async (app = null) => {
    if (!upiVpa) {
      toastError("Please enter UPI ID");
      return;
    }

    const validation = validateUPI(upiVpa);
    if (!validation.isValid) {
      toastError(validation.message);
      return;
    }

    if (currentTotal <= 0) {
      toastError("Invalid amount");
      return;
    }

    setLoading(true);

    cleanupIframe();

    try {
      const result = await processUPIIntent(
        currentTotal,
        upiVpa,
        order.orderId,
      );

      if (result?.success) {
        const txnId = result.data.transaction_id;
        setTransactionId(txnId);

        const intentUrl = generateUPIIntentUrl(
          txnId,
          upiVpa,
          currentTotal,
          app,
        );

        // If on mobile, try to open the app
        if (isMobile) {
          const iframe = document.createElement("iframe");
          iframe.style.display = "none";
          iframe.src = intentUrl;
          document.body.appendChild(iframe);
          iframeRef.current = iframe;

          let appOpened = false;

          const handleBlur = () => {
            appOpened = true;
            toastSuccess(`Opening ${app ? app.name : "UPI app"}...`);
            cleanupIframe();
            setShowUPIModal(false);
            setShowAppSelector(false);
            setUpiVpa("");
            startPolling(txnId);
          };

          blurListenerRef.current = handleBlur;
          window.addEventListener("blur", handleBlur, { once: true });

          timeoutRef.current = setTimeout(() => {
            if (!appOpened) {
              cleanupIframe();
              window.removeEventListener("blur", handleBlur);

              const shouldUseQR = window.confirm(
                `${app ? app.name : "UPI app"} could not be opened.\n\n` +
                  `Would you like to use QR Code payment instead?`,
              );

              if (shouldUseQR) {
                setShowAppSelector(false);
                setShowUPIModal(false);
                setActiveMethod("upi_qr");
                handleGenerateUPIQR();
                toastInfo("Please use QR code to complete payment");
              } else {
                setShowAppSelector(true);
                toastInfo("Please try another UPI app");
              }
            }
            timeoutRef.current = null;
          }, 2500);
        } else {
          // On desktop - show QR code with the UPI link
          setDesktopUPILink(intentUrl);
          setShowDesktopQR(true);
          setShowUPIModal(false);
          setShowAppSelector(false);
          toastInfo("Scan QR code with your mobile to pay");
        }
      } else {
        toastError(result?.message || "Failed to process payment");
        setPaymentStatus("failed");
      }
    } catch (error) {
      console.error("UPI payment error:", error);
      toastError("Payment failed. Please try again.");
      setPaymentStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  // Card Payment
  const handleCardPayment = async () => {
    const cleanCardNumber = cardNumber.replace(/\s/g, "");
    if (!validateCardNumber(cleanCardNumber)) {
      toastError(cardNumberError || "Invalid card number");
      return;
    }

    if (!validateExpiry(expiry)) {
      toastError(expiryError || "Invalid expiry date");
      return;
    }

    if (!/^[0-9]{3,4}$/.test(cvv)) {
      setCvvError("Enter valid CVV");
      toastError("Enter valid CVV");
      return;
    }

    if (currentTotal <= 0) {
      toastError("Invalid amount");
      return;
    }

    setLoading(true);

    try {
      const [expiryMonth, expiryYear] = expiry.split(" / ");

      const result = await processCardPayment({
        card_number: cleanCardNumber,
        expiry_month: expiryMonth,
        expiry_year: expiryYear,
        cvv: cvv,
        amount: currentTotal,
        order_id: order.orderId,
        cardholder_name: cardholderName || "Card Holder",
      });

      if (result?.success) {
        toastSuccess("Payment successful!");
        navigate(
          `/order-confirmation?orderId=${order.orderId}&transactionId=${result.data.transaction_id}`,
        );
      } else {
        toastError(result?.message || "Payment failed");
        setPaymentStatus("failed");
      }
    } catch (error) {
      console.error("Card payment error:", error);
      toastError("Card payment failed. Please try again.");
      setPaymentStatus("failed");
    } finally {
      setLoading(false);
    }
  };

  // COD
  const handleCOD = async () => {
    if (currentTotal <= 0) {
      toastError("Invalid amount");
      return;
    }

    setLoading(true);

    try {
      const result = await processCOD(currentTotal, order.orderId);

      if (result?.success) {
        toastSuccess("COD order placed successfully!");
        navigate(
          `/order-confirmation?orderId=${order.orderId}&transactionId=${result.data.transaction_id}`,
        );
      } else {
        toastError(result?.message || "COD order failed");
      }
    } catch (error) {
      console.error("COD error:", error);
      toastError("Failed to place COD order");
    } finally {
      setLoading(false);
    }
  };

  // Gift Card
  const handleApplyGift = async () => {
    if (!giftCode.trim()) {
      setGiftMessage(
        <span style={{ color: "#d32f2f" }}>Please enter gift card code</span>,
      );
      return;
    }

    setLoading(true);

    try {
      const result = await applyGiftCard(giftCode.toUpperCase());

      if (result?.success) {
        const discount = Math.min(
          result.data.balance,
          currentTotal + giftDiscount,
        );
        setGiftDiscount(discount);
        setGiftMessage(
          <span style={{ color: "#0e6245" }}>
            <i className="fas fa-check-circle"></i> Applied! -₹
            {discount.toFixed(2)}
          </span>,
        );
        toastSuccess(
          `Gift card applied! ₹${discount.toFixed(2)} discount added.`,
        );
        setGiftCode("");
      } else {
        setGiftMessage(
          <span style={{ color: "#d32f2f" }}>
            {result?.message || "Invalid or expired gift card"}
          </span>,
        );
      }
    } catch (error) {
      console.error("Gift card error:", error);
      setGiftMessage(
        <span style={{ color: "#d32f2f" }}>Failed to apply gift card</span>,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCvvChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 4) val = val.slice(0, 4);
    setCvv(val);
    if (cvvError) setCvvError("");
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardNumber(formatted);
    if (cardNumberError) validateCardNumber(formatted);
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiry(e.target.value);
    setExpiry(formatted);
    if (expiryError) validateExpiry(formatted);
  };

  const handleRetry = () => {
    setPaymentStatus(null);
    setQrCode(null);
    setTransactionId(null);
    if (activeMethod === "upi_qr") {
      handleGenerateUPIQR();
    } else if (activeMethod === "card") {
      setCvv("");
      setCardNumber("");
      setExpiry("");
    }
  };

  // Render payment options with accessibility
  const renderPaymentOptions = () => {
    const icons = {
      card: "fas fa-credit-card",
      upi_qr: "fas fa-qrcode",
      upi_intent: "fab fa-google-pay",
      cod: "fas fa-rupee-sign",
      gift: "fas fa-gift",
    };
    const names = {
      card: "Credit / Debit Card",
      upi_qr: "UPI QR Code",
      upi_intent: "UPI (Google Pay/PhonePe)",
      cod: "Cash on Delivery",
      gift: "Gift Card / Voucher",
    };

    return paymentMethodsList.map((method) => {
      const id = method.id;
      const enabled = method.enabled !== false;
      const isActive = activeMethod === id;

      return (
        <button
          key={id.toString()}
          className={`option-item ${isActive ? "active" : ""} ${!enabled ? "disabled" : ""}`}
          onClick={() => enabled && updateActiveTab(id)}
          disabled={!enabled}
          aria-label={`Select ${names[id] || method.name} payment method`}
          aria-current={isActive ? "step" : undefined}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            cursor: enabled ? "pointer" : "not-allowed",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            padding: "16px 20px",
            borderBottom: "1px solid #edeff2",
            transition: "all 0.2s ease",
          }}
        >
          <div className="option-icon" aria-hidden="true">
            <i className={icons[id] || "fas fa-credit-card"}></i>
          </div>
          <div className="option-text">
            {names[id] || method.name}
            {method.description && (
              <div style={{ fontSize: "12px", color: "#0f7b3a" }}>
                {method.description}
              </div>
            )}
          </div>
        </button>
      );
    });
  };

  const renderCenterPanel = () => {
    switch (activeMethod) {
      case "card":
        return (
          <div id="cardPanel">
            <div className="card-header">
              <div>
                <strong>Credit / Debit / ATM Card</strong>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  Add and secure cards as per RBI guidelines
                </div>
                <div style={{ fontSize: "12px", color: "green" }}>
                  Get upto 5% cashback • 2 offers available
                </div>
              </div>
            </div>

            <div className="card-note">
              Note: Please ensure your card can be used for online transactions.
            </div>

            <div className="card-form">
              <div className="form-group">
                <label htmlFor="cardNumber">Card Number</label>
                <input
                  type="text"
                  placeholder="XXXX XXXX XXXX XXXX"
                  maxLength="19"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  id="cardNumber"
                  className={cardNumberError ? "error" : ""}
                />
                {cardNumberError && (
                  <small className="error-text">{cardNumberError}</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="cardholderName">Cardholder Name</label>
                <input
                  type="text"
                  placeholder="Name on card"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  id="cardholderName"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="expiry">Valid Thru</label>
                  <input
                    type="text"
                    placeholder="MM / YY"
                    maxLength="9"
                    value={expiry}
                    onChange={handleExpiryChange}
                    id="expiry"
                    className={expiryError ? "error" : ""}
                  />
                  {expiryError && (
                    <small className="error-text">{expiryError}</small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="cvv">CVV</label>
                  <input
                    type="password"
                    placeholder="CVV"
                    maxLength="4"
                    value={cvv}
                    onChange={handleCvvChange}
                    id="cvv"
                    className={cvvError ? "error" : ""}
                  />
                  {cvvError && <small className="error-text">{cvvError}</small>}
                </div>
              </div>

              <button
                className="pay-btn"
                onClick={handleCardPayment}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  <>Pay ₹{currentTotal.toFixed(2)}</>
                )}
              </button>

              {paymentStatus === "failed" && (
                <div className="payment-failed">
                  <i className="fas fa-times-circle"></i> Payment failed.
                  <button onClick={handleRetry} className="retry-btn-small">
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case "upi_qr":
        return (
          <div className="placeholder-method">
            <i className="fas fa-qrcode" style={{ fontSize: "2rem" }}></i>
            <h4>UPI QR Code Payment</h4>
            <p>Scan QR code with any UPI app to pay</p>
            {qrCode ? (
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <div className="qr-container">
                  <QRCodeCanvas value={qrCode} size={200} />
                </div>
                <p style={{ marginTop: "10px" }}>
                  <i className="fas fa-mobile-alt"></i> Scan with any UPI app
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  Amount: ₹{currentTotal.toFixed(2)}
                </p>
                <p style={{ fontSize: "12px", color: "#666" }}>
                  Order ID: {order.orderId}
                </p>
                <p style={{ fontSize: "12px", color: "#666" }}>
                  Transaction ID: {transactionId}
                </p>
                {paymentStatus === "completed" && (
                  <div className="payment-success">
                    <i className="fas fa-check-circle"></i> Payment completed!
                  </div>
                )}
                {paymentStatus === "failed" && (
                  <div className="payment-failed">
                    <i className="fas fa-times-circle"></i> Payment failed.
                    <button onClick={handleRetry} className="retry-btn">
                      Try Again
                    </button>
                  </div>
                )}
                {!paymentStatus && (
                  <button
                    className="retry-btn"
                    onClick={handleGenerateUPIQR}
                    disabled={loading}
                  >
                    Generate New QR
                  </button>
                )}
              </div>
            ) : (
              <button
                className="pay-btn"
                onClick={handleGenerateUPIQR}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Generating...
                  </>
                ) : (
                  "Generate QR Code"
                )}
              </button>
            )}
          </div>
        );

      case "upi_intent":
        return (
          <div className="placeholder-method">
            <div className="upi-icons">
              <i className="fab fa-google-pay"></i>
              <i className="fas fa-mobile-alt"></i>
              <i className="fas fa-rupee-sign"></i>
              <i className="fab fa-amazon-pay"></i>
            </div>
            <h4>UPI Payment</h4>
            {isMobile ? (
              <>
                <p>
                  Pay directly from Google Pay, PhonePe, Paytm, or any UPI app
                </p>
                <button
                  className="pay-btn"
                  onClick={() => {
                    setShowUPIModal(true);
                    setShowAppSelector(false);
                  }}
                  disabled={loading}
                  style={{ background: "#2874f0" }}
                >
                  <i className="fab fa-google-pay"></i> Pay with UPI
                </button>
              </>
            ) : (
              <>
                <div className="desktop-warning">
                  <i
                    className="fas fa-qrcode"
                    style={{ fontSize: "2rem", color: "#facc15" }}
                  ></i>
                  <p style={{ marginTop: "10px" }}>
                    <strong>Desktop Detected!</strong>
                    <br />
                    Click below to generate QR code. Scan with your mobile to
                    pay instantly.
                  </p>
                </div>
                <button
                  className="pay-btn"
                  onClick={async () => {
                    setLoading(true);
                    try {
                      const result = await processUPIIntent(
                        currentTotal,
                        "customer@okhdfcbank",
                        order.orderId,
                      );
                      if (result?.success) {
                        setDesktopUPILink(result.data.upi_intent_url);
                        setShowDesktopQR(true);
                        setTransactionId(result.data.transaction_id);
                        startPolling(result.data.transaction_id);
                        toastInfo("Scan QR code with your mobile to pay");
                      } else {
                        toastError("Failed to generate payment link");
                      }
                    } catch (error) {
                      toastError("Payment failed");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  style={{ background: "#facc15", marginTop: "10px" }}
                >
                  {loading ? "Generating..." : "Generate Mobile Payment QR"}
                </button>
              </>
            )}
            {giftDiscount > 0 && (
              <div className="discount-applied" style={{ marginTop: "15px" }}>
                <i className="fas fa-gift"></i> Gift card discount applied: -₹
                {giftDiscount.toFixed(2)}
              </div>
            )}
            {paymentStatus === "failed" && (
              <div className="payment-failed">
                <i className="fas fa-times-circle"></i> Payment failed. Please
                try again.
              </div>
            )}
          </div>
        );

      case "cod":
        return (
          <div className="placeholder-method">
            <i className="fas fa-truck" style={{ fontSize: "2rem" }}></i>
            <h4>Cash on Delivery</h4>
            <p>
              Pay ₹{currentTotal.toFixed(2)} when your order arrives. Keep exact
              change handy.
            </p>
            {giftDiscount > 0 && (
              <div className="discount-applied">
                <i className="fas fa-gift"></i> Gift card discount: -₹
                {giftDiscount.toFixed(2)}
              </div>
            )}
            <button
              className="pay-btn"
              onClick={handleCOD}
              disabled={loading}
              style={{ background: "#facc15", marginTop: "20px" }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Processing...
                </>
              ) : (
                "Confirm COD Order"
              )}
            </button>
          </div>
        );

      case "gift":
        return (
          <div className="gift-card-section">
            <div className="gift-header">
              <i
                className="fas fa-gift"
                style={{ color: "#f9a825", fontSize: "2rem" }}
              ></i>
              <h3>Apply Gift Card</h3>
              <p>Enter your gift card code</p>
            </div>
            <div className="gift-input-group">
              <input
                type="text"
                placeholder="Enter gift card code"
                value={giftCode}
                onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                disabled={loading}
              />
              <button
                className="apply-gift-btn"
                onClick={handleApplyGift}
                disabled={loading}
              >
                {loading ? "Applying..." : "Apply"}
              </button>
            </div>
            <div className="gift-message">{giftMessage}</div>
            {giftDiscount > 0 && (
              <div className="gift-balance">
                <i className="fas fa-wallet"></i> Discount applied: ₹
                {giftDiscount.toFixed(2)}
                <br />
                <small>Final amount: ₹{currentTotal.toFixed(2)}</small>
              </div>
            )}
            <div className="gift-info">
              <i className="fas fa-info-circle"></i> Gift cards are
              non-refundable
            </div>
          </div>
        );

      default:
        return (
          <div className="placeholder-method">Select a payment method</div>
        );
    }
  };

  return (
    <div className="container">
      <div className="payment-container">
        <style>{`
          .payment-container { margin: 20px 0 30px 0; background: #fff; border-radius: 5px; overflow: hidden; padding: 28px 32px 40px 32px; }
          .page-heading { font-size: 1.8rem; font-weight: 600; margin-bottom: 28px; color: #1a1c1e; letter-spacing: -0.3px; border-left: 5px solid #facc15; padding-left: 18px; }
          .payment-layout { display: grid; grid-template-columns: 280px 1fr 340px; gap: 28px; align-items: start; }
          .payment-options { background: #ffffff; border-radius: 18px; border: 1px solid #eef2f6; overflow: hidden; }
          .option-item { display: flex; align-items: center; gap: 14px; padding: 16px 20px; border-bottom: 1px solid #edeff2; transition: all 0.2s ease; font-family: inherit; font-size: inherit; width: 100%; }
          .option-item:hover:not(:disabled) { background-color: #fafafc; }
          .option-item.active { background: #fffbf0; border-left: 3px solid #facc15; }
          .option-item.disabled, .option-item:disabled { opacity: 0.5; cursor: not-allowed; }
          .option-icon { font-size: 1.45rem; width: 32px; color: #3e4152; }
          .option-text { font-weight: 500; font-size: 14px; color: #1f2a3e; flex: 1; text-align: left; }
          .payment-detail-panel { background: #ffffff; border-radius: 20px; border: 1px solid #eef2f6; padding: 20px 24px; }
          .card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; flex-wrap: wrap; }
          .card-note { font-size: 12px; color: #666; margin: 10px 0; }
          .card-form { background: #f5f5f5; padding: 20px; border-radius: 10px; }
          .form-group { margin-bottom: 15px; }
          .form-group label { font-size: 13px; font-weight: 500; display: block; margin-bottom: 5px; }
          .form-group input { width: 100%; padding: 10px; border-radius: 8px; border: 1px solid #ccc; font-size: 14px; }
          .form-group input.error { border-color: #d32f2f; }
          .error-text { color: #d32f2f; font-size: 11px; margin-top: 5px; display: block; }
          .form-row { display: flex; gap: 10px; }
          .form-row .form-group { flex: 1; }
          .pay-btn { width: 100%; background: #facc15; border: none; padding: 12px; border-radius: 8px; font-weight: bold; cursor: pointer; margin-top: 10px; font-size: 16px; }
          .pay-btn:hover:not(:disabled) { background: #e5b800; }
          .pay-btn:disabled { opacity: 0.6; cursor: not-allowed; }
          .placeholder-method { text-align: center; padding: 40px 20px; color: #5c6874; display: grid; gap: 14px; }
          .upi-icons i { font-size: 2rem; margin: 0 8px; color: #3e4152; }
          .qr-container { display: inline-block; padding: 10px; background: white; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
          .payment-success { color: #2e7d32; margin-top: 15px; font-weight: bold; padding: 10px; background: #e8f5e9; border-radius: 8px; }
          .payment-failed { color: #d32f2f; margin-top: 15px; font-weight: bold; padding: 10px; background: #ffebee; border-radius: 8px; }
          .retry-btn { background: #2874f0; color: white; border: none; padding: 8px 16px; border-radius: 20px; cursor: pointer; margin-top: 10px; margin-left: 10px; }
          .retry-btn-small { background: #2874f0; color: white; border: none; padding: 4px 12px; border-radius: 15px; cursor: pointer; margin-left: 10px; font-size: 12px; }
          .retry-btn:hover, .retry-btn-small:hover { background: #1a5bc4; }
          .gift-card-section { display: flex; flex-direction: column; gap: 18px; }
          .gift-header { text-align: center; }
          .gift-header h3 { margin: 10px 0 5px; }
          .gift-input-group { display: flex; gap: 10px; }
          .gift-input-group input { flex: 2; padding: 12px; border-radius: 14px; border: 1px solid #ccd7e4; outline: none; font-size: 14px; }
          .gift-input-group input:focus { border-color: #facc15; }
          .apply-gift-btn { background: #2874f0; color: white; border: none; border-radius: 40px; padding: 0 20px; font-weight: 500; cursor: pointer; }
          .apply-gift-btn:hover:not(:disabled) { background: #1a5bc4; }
          .gift-message { font-size: 12px; text-align: center; }
          .gift-balance { background: #e8f5e9; padding: 10px; border-radius: 8px; text-align: center; }
          .gift-info { font-size: 12px; color: #666; text-align: center; }
          .order-summary { background: #fafbfe; border-radius: 20px; padding: 20px 22px; border: 1px solid #eef2f6; }
          .summary-row { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 12px; color: #3e4152; }
          .summary-row.discount { color: #388e3c; }
          .summary-fees { font-size: 12px; color: #878f9c; border-top: 1px dashed #e2e8f0; margin-top: 6px; padding-top: 8px; }
          .total-row { font-weight: 800; font-size: 1.35rem; margin-top: 12px; border-top: 2px solid #e2e8f0; padding-top: 16px; display: flex; justify-content: space-between; }
          .instant-offer { background: #eef7ff; border-radius: 14px; padding: 12px; margin-top: 20px; display: flex; align-items: center; justify-content: space-between; }
          .offer-tag { background: #facc15; border-radius: 20px; padding: 4px 10px; font-weight: 700; font-size: 12px; }
          hr { margin: 14px 0; border: none; border-top: 1px solid #eef2f6; }
          .desktop-warning { background: #e3f2fd; border-radius: 12px; padding: 20px; text-align: center; margin: 10px 0; }
          .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; backdrop-filter: blur(5px); }
          .modal-content { background: white; border-radius: 24px; padding: 0; width: 90%; max-width: 500px; max-height: 90vh; overflow-y: auto; animation: slideUp 0.3s ease; }
          @keyframes slideUp { from { transform: translateY(50px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          .modal-header { padding: 24px 24px 16px 24px; border-bottom: 1px solid #eef2f6;position: relative !important;justify-content: space-between !important;}
          .modal-header h3 { margin: 0; font-size: 1.4rem; display: flex; align-items: center; gap: 10px; }
          .modal-body { padding: 24px; }
          .modal-footer { padding: 16px 24px 24px 24px; border-top: 1px solid #eef2f6; display: flex; gap: 12px; justify-content: flex-end; }
          .upi-input { width: 100%; padding: 14px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 14px; transition: all 0.2s; }
          .upi-input:focus { outline: none; border-color: #2874f0; box-shadow: 0 0 0 3px rgba(40,116,240,0.1); }
          .format-hint { font-size: 11px; color: #666; margin-top: 8px; display: flex; align-items: center; gap: 6px; }
          .app-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); gap: 12px; margin: 20px 0; }
          .app-card { background: white; border: 2px solid #eef2f6; border-radius: 16px; cursor: pointer; padding: 16px; display: flex; flex-direction: column; align-items: center; gap: 8px; transition: all 0.2s ease; }
          .app-card:hover:not(:disabled) { border-color: #2874f0; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
          .app-card:disabled { opacity: 0.5; cursor: not-allowed; }
          .app-icon { font-size: 2.5rem; }
          .app-name { font-size: 12px; font-weight: 600; }
          .app-status { font-size: 10px; }
          .installed { color: #2e7d32; }
          .not-installed { color: #d32f2f; }
          .modal-buttons { display: flex; gap: 12px; justify-content: flex-end; }
          .modal-cancel { padding: 10px 20px; background: #f0f0f0; border: none; border-radius: 10px; cursor: pointer; font-size: 14px; }
          .modal-cancel:hover { background: #e0e0e0; }
          .modal-pay { padding: 10px 24px; background: #2874f0; color: white; border: none; border-radius: 10px; cursor: pointer; font-size: 14px; font-weight: 600; }
          .modal-pay:hover:not(:disabled) { background: #1a5bc4; }
          .modal-pay:disabled { opacity: 0.6; cursor: not-allowed; }
          .discount-applied { background: #e8f5e9; color: #2e7d32; padding: 8px 12px; border-radius: 8px; margin-top: 10px; font-size: 13px; }
          .fallback-message { background: #fff3e0; padding: 12px; border-radius: 10px; margin-top: 16px; font-size: 12px; color: #e65100; display: flex; align-items: center; gap: 8px; }
          .qr-fallback { margin-top: 16px; padding-top: 16px; border-top: 1px solid #eef2f6; text-align: center; }
          @media (max-width: 980px) { .payment-layout { grid-template-columns: 1fr; gap: 24px; } .payment-container { padding: 0px; } }
          @media (max-width: 550px) { .form-row { flex-direction: column; } .app-grid { grid-template-columns: repeat(3, 1fr); } }
        `}</style>

        <div className="page-heading">
          <i
            className="fas fa-lock"
            style={{ fontSize: "1.3rem", marginRight: "8px", color: "#facc15" }}
          ></i>
          Complete Payment
          {!!order.orderId && (
            <span
              style={{ fontSize: "12px", marginLeft: "15px", color: "#666" }}
            >
              Order: #{order.orderId}
            </span>
          )}
        </div>

        <div className="payment-layout">
          <div className="payment-options">{renderPaymentOptions()}</div>
          <div className="payment-detail-panel">{renderCenterPanel()}</div>
          <OrderSummaryComponent
            order={order}
            giftDiscount={giftDiscount}
            currentTotal={currentTotal}
          />
        </div>
      </div>

      {/* Desktop QR Modal */}
      {showDesktopQR && desktopUPILink && (
        <div className="modal-overlay" onClick={() => setShowDesktopQR(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-qrcode"></i>
                Scan & Pay with Mobile
              </h3>
            </div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div className="qr-container" style={{ marginBottom: "20px" }}>
                <QRCodeCanvas value={desktopUPILink} size={250} />
              </div>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  marginTop: "10px",
                }}
              >
                Amount: ₹{currentTotal.toFixed(2)}
              </p>
              <p style={{ fontSize: "12px", color: "#666" }}>
                Open any UPI app on your mobile and scan this QR code
              </p>
              <div className="fallback-message" style={{ marginTop: "15px" }}>
                <i className="fas fa-info-circle"></i>
                <div>
                  <strong>After payment:</strong>
                  <br />
                  Payment will be automatically detected in few seconds
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDesktopQR(false)}
                className="modal-cancel"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UPI ID Input Modal (Mobile Only) */}
      {isMobile && showUPIModal && !showAppSelector && (
        <div className="modal-overlay" onClick={() => setShowUPIModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i
                  className="fab fa-google-pay"
                  style={{ color: "#4285f4" }}
                ></i>
                Enter UPI ID
              </h3>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="e.g., 000000@okhdfcbank"
                value={upiVpa}
                onChange={(e) => setUpiVpa(e.target.value)}
                className="upi-input"
                autoFocus
              />
              <div className="format-hint">
                <i className="fas fa-info-circle"></i>
                Format: mobilenumber@bankname or username@bankname
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowUPIModal(false)}
                className="modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUPIModal(false);
                  setShowAppSelector(true);
                }}
                className="modal-pay"
                disabled={!upiVpa}
              >
                Next: Select App →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* App Selection Modal (Mobile Only) */}
      {isMobile && showAppSelector && (
        <div
          className="modal-overlay"
          onClick={() => setShowAppSelector(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-mobile-alt"></i>
                Select UPI App
              </h3>
              <p
                style={{ margin: "8px 0 0 0", fontSize: "13px", color: "#666" }}
              >
                UPI ID: <strong>{upiVpa}</strong>
              </p>
            </div>
            <div className="modal-body">
              <div className="app-grid">
                {UPI_APPS.map((app) => (
                  <button
                    key={app.id}
                    className="app-card"
                    onClick={() => !loading && handleUPIPayment(app)}
                    disabled={loading}
                    style={{
                      background: "white",
                      border: "2px solid #eef2f6",
                      borderRadius: "16px",
                      cursor: loading ? "not-allowed" : "pointer",
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                      width: "100%",
                    }}
                  >
                    <div
                      className="app-icon"
                      style={{ color: app.color }}
                      aria-hidden="true"
                    >
                      <i className={app.icon}></i>
                    </div>
                    <div className="app-name">{app.name}</div>
                    <div className="app-status">
                      {appInstallStatus[app.id] ? (
                        <span className="installed">
                          <i className="fas fa-check-circle"></i> Installed
                        </span>
                      ) : (
                        <span className="not-installed">
                          <i className="fas fa-exclamation-triangle"></i> May
                          not be installed
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="fallback-message">
                <i className="fas fa-info-circle"></i>
                <div>
                  <strong>App not opening?</strong>
                  <br />
                  Click on any app above. If it doesn't open, we'll show you
                  alternative payment options.
                </div>
              </div>

              <div className="qr-fallback">
                <button
                  className="modal-cancel"
                  onClick={() => {
                    setShowAppSelector(false);
                    setActiveMethod("upi_qr");
                    handleGenerateUPIQR();
                  }}
                  style={{ width: "100%" }}
                >
                  <i className="fas fa-qrcode"></i> Use QR Code Instead
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowAppSelector(false);
                  setShowUPIModal(true);
                }}
                className="modal-cancel"
              >
                Back
              </button>
              <button
                onClick={() => handleUPIPayment(null)}
                className="modal-pay"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Processing...
                  </>
                ) : (
                  "Use Default UPI"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentPage;
